import type { TFunction } from 'i18next';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '~/components/common/buttons/Button';
import type { IconType } from '~/components/ui/Icons/Icons';
import SVGIcon from '~/components/ui/Icons/SVGIcon';
import type { OfficeInfoItem, officeInfoType } from '~/types/offices';
import mixpanelTrack from '~/hooks/useMixPanel';

const getOfficeItemText = (item: OfficeInfoItem, t: TFunction): string | undefined => {
  if (item.countService !== null && (item.countService > 1 || item.countService === 0)) {
    return `${item.countService} ${t(`office.ammenities.${item.name}.other`)}`;
  }
  if (item.countService === 1) {
    return `${item.countService} ${t(`office.ammenities.${item.name}.one`)}`;
  }
  if (item.selectedValue) {
    return t(`office.ammenities.${item.name}`);
  }
  return undefined;
};

const Service = ({ officeInfo }: { officeInfo: officeInfoType }) => {
  const { t } = useTranslation();
  const [showMore, setShowMore] = useState(false);

  const availableServices = officeInfo
    .filter((item) => (item.countService !== null && item.countService > 0) || item.selectedValue)
    .sort((a, b) => a.position - b.position)
    .map((item, index) => ({ index: index, ...item }));

  return (
    <div className="mt-8 pt-8 fo-border-top-sm border-grey-200">
      <h3 className="fo-display-3 -medium mb-6">{t('office.show.services.title')}</h3>
      <div className="row">
        {/* First column */}
        <div className="col-12 col-sm-6">
          {availableServices.slice(0, Math.ceil(availableServices.length / 2)).map((item) => {
            const displayText = getOfficeItemText(item, t);
            if (displayText) {
              return (
                <div
                  className={`mb-4 ${!showMore && item.index > 5 ? 'd-none d-sm-block' : ''}`}
                  key={item.fieldName}
                >
                  <SVGIcon iconType={item.icon as IconType} />
                  <span className="ms-4 text-1">{displayText}</span>
                </div>
              );
            }
            return null;
          })}
        </div>

        {/* Second column */}
        <div className="col-12 col-sm-6">
          {availableServices.slice(Math.ceil(availableServices.length / 2)).map((item) => {
            const displayText = getOfficeItemText(item, t);
            if (displayText) {
              return (
                <div
                  className={`mb-4 ${!showMore && item.index > 5 ? 'd-none d-sm-block' : ''}`}
                  key={item.fieldName}
                >
                  <SVGIcon iconType={item.icon as IconType} />
                  <span className="ms-4 text-1">{displayText}</span>
                </div>
              );
            }
            return null;
          })}
        </div>
      </div>

      {availableServices.length > 5 && (
        <Button
          extraClasses="w-100 d-sm-none"
          variant="primaryOutline"
          size="md"
          onClick={() => {
            if (showMore) mixpanelTrack('E.read_more_collapse');
            else mixpanelTrack('E.read_more_expand');
            setShowMore(!showMore);
          }}
        >
          <span className="me-2">
            {showMore ? t('office.show.services.showLess') : t('office.show.services.showAll')}
          </span>
          <SVGIcon iconType={showMore ? 'chevron-up' : 'chevron-down'} />
        </Button>
      )}
    </div>
  );
};

export default Service;
